/**
 * Every browser window of Connect has its own tab bar (I-3641). A window knows
 * itself by an id in its address (`?window=`) and in the browser's session, so a
 * reload and the browser's „reopen closed tab“ find its tabs again. A new
 * browser tab without one starts fresh. Windows that are not open any more are
 * listed as closed and can come back.
 */
import type { StoredTiles, StoredWindow } from "./useTileWorkspaces";

const PARAM = "window";
const SESSION_KEY = "connect.tiles.window";
const CHANNEL = "connect.tiles.windows";
/** A closed window is kept this long after its last change. */
export const CLOSED_WINDOW_DAYS = 14;
/** At most this many closed windows are offered. */
export const CLOSED_WINDOW_LIMIT = 10;

const newId = () => crypto.randomUUID();

function readSession(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function writeSession(id: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    // Without session storage the address still carries the id.
  }
}

/** The id this browser window goes by; null before the shell took one. Also from a tile. */
export function readWindowId(): string | null {
  return new URLSearchParams(location.search).get(PARAM) ?? readSession();
}

function adopt(id: string): void {
  writeSession(id);
  const url = new URL(location.href);
  if (url.searchParams.get(PARAM) === id) return;
  url.searchParams.set(PARAM, id);
  history.replaceState(history.state, "", url);
}

/** The address of a window: the shell, its backend choice, the window, its tabs. */
export function windowUrl(id: string): string {
  const query = new URLSearchParams(location.search);
  query.delete("tile");
  query.set(PARAM, id);
  return `${location.pathname}?${query}#/tiles`;
}

/** A page in a browser tab of its own: same backend, no tile, a window of its own. */
export function browserTabUrl(path: string): string {
  const query = new URLSearchParams(location.search);
  query.delete("tile");
  query.delete(PARAM);
  const search = query.toString();
  return `${location.pathname}${search ? `?${search}` : ""}#${path}`;
}

let channel: BroadcastChannel | null = null;
let ownId: string | null = null;
let claiming: { id: string; token: string } | null = null;

function openChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === "undefined") return channel;
  channel = new BroadcastChannel(CHANNEL);
  channel.addEventListener("message", (event: MessageEvent) => {
    if (event.data?.type !== "ping") return;
    if (ownId) channel?.postMessage({ type: "pong", id: ownId });
    // Two pages claiming one id at once: the lower token keeps it.
    else if (
      claiming &&
      event.data.claim?.id === claiming.id &&
      claiming.token < event.data.claim.token
    )
      channel?.postMessage({ type: "pong", id: claiming.id });
  });
  return channel;
}

/** The ids of the Connect windows open in this browser right now, this one aside. */
export function liveWindows(waitMs = 250): Promise<Set<string>> {
  const listener = openChannel();
  if (!listener) return Promise.resolve(new Set());
  const ask = new BroadcastChannel(CHANNEL);
  const live = new Set<string>();
  return new Promise((resolve) => {
    ask.addEventListener("message", (event: MessageEvent) => {
      if (event.data?.type === "pong" && typeof event.data.id === "string")
        live.add(event.data.id);
    });
    ask.postMessage({ type: "ping", claim: claiming });
    setTimeout(() => {
      ask.close();
      // This page's own channel answers too.
      if (ownId) live.delete(ownId);
      resolve(live);
    }, waitMs);
  });
}

/**
 * Put this window's id into its address before the router starts: the router
 * keeps the address it found and would drop a later one. A tile is no window.
 */
export function prepareWindowId(): void {
  if (new URLSearchParams(location.search).has("tile")) return;
  adopt(readWindowId() ?? newId());
}

/**
 * Take this window's id. Another open window going by it is a duplicated browser
 * tab: this one starts again as a new window.
 */
export async function claimWindowId(): Promise<string> {
  const known = readWindowId() ?? newId();
  // Back after being switched off: this page has answered to it all along.
  if (ownId === known) return known;
  claiming = { id: known, token: newId() };
  const live = await liveWindows();
  claiming = null;
  if (live.has(known)) {
    const id = newId();
    writeSession(id);
    location.replace(`${windowUrl(id).split("#")[0]}${location.hash}`);
    return new Promise(() => {});
  }
  ownId = known;
  openChannel();
  adopt(known);
  return known;
}

/** A new id for a window that is about to open. */
export function newWindowId(): string {
  return newId();
}

export interface ClosedWindow {
  id: string;
  savedAt: string;
  tabs: string[];
}

/** What lay in a window, each thing once: „3× ERP, Mail“. */
export function closedContent(entry: ClosedWindow): string {
  const counts = new Map<string, number>();
  for (const tab of entry.tabs)
    for (const part of tab.split(" + ").filter(Boolean))
      counts.set(part, (counts.get(part) ?? 0) + 1);
  return [...counts]
    .map(([name, count]) => (count > 1 ? `${count}× ${name}` : name))
    .join(", ");
}

const savedTime = (entry: StoredWindow | undefined) =>
  Date.parse(entry?.savedAt ?? "") || 0;

/**
 * Windows stored but not open, newest first, with what lay in their tabs
 * (`labelOf` is empty for a tab with nothing to name).
 */
export function closedWindows(
  stored: StoredTiles | null | undefined,
  open: ReadonlySet<string>,
  labelOf: (workspaceId: string) => string,
): ClosedWindow[] {
  return (
    Object.entries(stored?.windows ?? {})
      .map(([id, value]) => ({ id, entry: value?.[0] }))
      .filter(({ id, entry }) => entry && !open.has(id) && entry.order?.length)
      .sort((a, b) => savedTime(b.entry) - savedTime(a.entry))
      .map(({ id, entry }) => ({
        id,
        savedAt: entry!.savedAt ?? "",
        tabs: entry!.order.map(labelOf),
      }))
      // A window that only showed the start page is nothing to bring back.
      .filter((window) => window.tabs.some(Boolean))
      .slice(0, CLOSED_WINDOW_LIMIT)
  );
}

/** The patch that forgets closed windows and their tabs for good. */
export function forgetWindowsPatch(
  stored: StoredTiles | null | undefined,
  ids: readonly string[],
): Record<string, unknown> {
  const windows: Record<string, null> = {};
  const workspaces: Record<string, null> = {};
  for (const id of ids) {
    windows[id] = null;
    stored?.windows?.[id]?.[0]?.order?.forEach((workspaceId) => {
      workspaces[workspaceId] = null;
    });
  }
  return { windows, workspaces };
}

/**
 * What a load clears from `shell.tiles`: closed windows (not `open` ones) past
 * their time or past the limit, tabs no window holds any more, and the keys of the single bar
 * there was before windows. Null when nothing is to clear.
 */
export function cleanupPatch(
  stored: StoredTiles | null | undefined,
  current: string,
  open: ReadonlySet<string> = new Set(),
  now = Date.now(),
): Record<string, unknown> | null {
  if (!stored) return null;
  const patch: Record<string, unknown> = {};
  const legacy = stored as Record<string, unknown>;
  for (const key of ["order", "activeId", "groups"])
    if (legacy[key] !== undefined) patch[key] = null;
  const windows = Object.entries(stored.windows ?? {});
  const oldest = now - CLOSED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const keptClosed = new Set(
    windows
      .filter(([id]) => id !== current && !open.has(id))
      .sort(([, a], [, b]) => savedTime(b?.[0]) - savedTime(a?.[0]))
      .slice(0, CLOSED_WINDOW_LIMIT)
      .filter(([, value]) => savedTime(value?.[0]) >= oldest)
      .map(([id]) => id),
  );
  const dropped: Record<string, null> = {};
  const held = new Set<string>();
  // Before windows: the tabs of the single bar move into the current window.
  if (!stored.windows && Array.isArray(legacy.order))
    (legacy.order as string[]).forEach((workspaceId) => held.add(workspaceId));
  for (const [id, value] of windows) {
    if (id === current || open.has(id) || keptClosed.has(id))
      value?.[0]?.order?.forEach((workspaceId) => held.add(workspaceId));
    else dropped[id] = null;
  }
  if (Object.keys(dropped).length) patch.windows = dropped;
  const orphans: Record<string, null> = {};
  for (const id of Object.keys(stored.workspaces ?? {}))
    if (!held.has(id)) orphans[id] = null;
  if (Object.keys(orphans).length) patch.workspaces = orphans;
  return Object.keys(patch).length ? patch : null;
}
